import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") return setState("paid");
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch { /* keep trying */ }
      if (++tries >= 8) return setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <SiteShell testId="payment-success-page" title="Payment — GoBuildWeb">
      <section className="bg-ink-950 pt-[72px] min-h-[70vh] grid place-items-center">
        <div className="mx-auto max-w-lg px-5 text-center py-20">
          {state === "checking" && (
            <div data-testid="pay-checking">
              <Loader2 className="mx-auto animate-spin text-gold-500" size={40} />
              <p className="mt-5 text-zinc-300">Confirming your payment…</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="pay-paid">
              <CheckCircle2 className="mx-auto text-emerald-400" size={56} />
              <h1 className="mt-5 font-jakarta text-3xl font-extrabold text-white">You're all set! 🎉</h1>
              <p className="mt-3 text-zinc-300">Thanks for your purchase{info?.label ? ` of the ${info.label}` : ""}. Our team will email you shortly to kick things off.</p>
              <button onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-xl bg-royal-600 hover:bg-royal-700 px-6 py-3.5 font-bold text-ink-950 transition shadow-gold" data-testid="pay-home">Back to Home <ArrowRight size={18} /></button>
            </div>
          )}
          {state === "pending" && (
            <div data-testid="pay-pending">
              <Loader2 className="mx-auto animate-spin text-gold-500" size={40} />
              <h1 className="mt-5 font-jakarta text-2xl font-extrabold text-white">Payment processing…</h1>
              <p className="mt-3 text-zinc-300">This can take a moment. We'll email your receipt once it clears.</p>
              <button onClick={() => navigate("/")} className="mt-8 rounded-xl border border-white/20 px-6 py-3.5 font-semibold text-white hover:bg-white/5 transition">Back to Home</button>
            </div>
          )}
          {state === "error" && (
            <div data-testid="pay-error">
              <XCircle className="mx-auto text-red-400" size={48} />
              <h1 className="mt-5 font-jakarta text-2xl font-extrabold text-white">We couldn't confirm this payment.</h1>
              <p className="mt-3 text-zinc-300">If you were charged, don't worry — contact us and we'll sort it out right away.</p>
              <button onClick={() => navigate("/contact")} className="mt-8 rounded-xl bg-royal-600 hover:bg-royal-700 px-6 py-3.5 font-bold text-ink-950 transition shadow-gold">Contact Us</button>
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
